import type { Document } from "@contentful/rich-text-types";
import type { ContentfulMediaImage } from "@scott/contentful/types";
import {
  CLOUDINARY_TRANSFORMATION_D2C_SQUARE,
  extractCloudinaryAssetUrl,
  getBlockId,
  getCloudinarySrcSet,
  resolveTransform,
} from "@/services/contentful/assemblies/page/adapter/utils";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type { SectionQuotePageBlock } from "@/types/page-block";

// The cite avatar renders at a fixed 60px square (see quote.tsx), so only small
// DPR variants are needed rather than the full responsive width range.
// biome-ignore lint/style/noMagicNumbers: avatar DPR widths (1x/2x/3x of 60px)
const QUOTE_AVATAR_WIDTHS = [60, 120, 180];
const QUOTE_AVATAR_DEFAULT_WIDTH = 120;

type ContentfulAthlete = {
  firstName?: string;
  lastName?: string;
  team?: string;
  image?: ContentfulMediaImage;
};

export interface SectionQuoteInput {
  sys?: { id: string; locale?: string };
  __typename: "SectionQuote";
  internalName: string;
  eyebrow?: string;
  quoteText: { json: Document };
  quoteType: "athlete" | "mission";
  authorName?: string;
  authorTeam?: string;
  authorImage?: ContentfulMediaImage;
  athlete?: ContentfulAthlete;
  uiConfiguration?: UiConfigurationInput;
}

function getAthleteName(athlete?: ContentfulAthlete): string | undefined {
  const firstName = athlete?.firstName?.trim();
  const lastName = athlete?.lastName?.trim();

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  return firstName || lastName;
}

export function SectionQuoteAdapter(
  item: SectionQuoteInput,
  index: number
): SectionQuotePageBlock {
  const athleteName = getAthleteName(item.athlete);

  const citeName = athleteName || item.authorName?.trim();
  const citeTeam = item.athlete?.team?.trim() || item.authorTeam?.trim();
  const citeImageAlt =
    item.athlete?.image?.alternativeText ||
    item.authorImage?.alternativeText ||
    citeName;

  const authorImage = extractCloudinaryAssetUrl(item.authorImage?.image);
  const athleteImage = extractCloudinaryAssetUrl(item.athlete?.image?.image);

  const citeImageSrc = athleteImage || authorImage;
  const uiConfig = item.uiConfiguration
    ? UiConfigurationAdapter(item.uiConfiguration)
    : undefined;

  const hasFocalPoint =
    item.authorImage?.hasFocalPoint || item.athlete?.image?.hasFocalPoint;
  const citeImageResponsive = citeImageSrc
    ? getCloudinarySrcSet(citeImageSrc, {
        transform: resolveTransform(
          CLOUDINARY_TRANSFORMATION_D2C_SQUARE,
          hasFocalPoint
        ),
        widths: QUOTE_AVATAR_WIDTHS,
        defaultWidth: QUOTE_AVATAR_DEFAULT_WIDTH,
      })
    : null;

  return {
    sys: item.sys,
    id: getBlockId(item, index),
    __typename: item.__typename,
    internalName: item.internalName,
    variant: item.quoteType,
    quote: item.quoteText,
    label: item.eyebrow ?? undefined,
    cite:
      citeName && citeTeam
        ? {
            name: citeName,
            team: citeTeam,
            image: citeImageSrc
              ? {
                  src: citeImageResponsive?.src ?? citeImageSrc,
                  srcSet: citeImageResponsive?.srcSet,
                  alt: citeImageAlt ?? undefined,
                }
              : undefined,
          }
        : undefined,
    background: uiConfig?.backgroundColor,
    paddingTop: uiConfig?.paddingTop,
    paddingBottom: uiConfig?.paddingBottom,
  };
}
